import { isAirAdminRole } from '@airreading/common';
import { type LoggedInUser } from '@airreading/common/data';
import { LDSingleKindContext, useLDClient } from 'launchdarkly-react-client-sdk';

import { ClientUsableFeatureFlagName, ClientUsableFeatureFlagRegistry } from './registry.js';

export function useFeatureFlag<T extends ClientUsableFeatureFlagName>(
  key: T,
  defaultValue: ClientUsableFeatureFlagRegistry[T],
): ClientUsableFeatureFlagRegistry[T] {
  const client = useLDClient();
  if (!client) {
    return defaultValue;
  }

  return client.variation(key, defaultValue) as ClientUsableFeatureFlagRegistry[T];
}

export interface ClientFeatureFlagUserContext extends LDSingleKindContext {
  kind: 'user';

  // Below are Client-side user context specific attributes
  // Note that LaunchDarkly UI portal has limited capability to parse nested JSON object/array
  // So we would prefer to flatten the user context for easier targeting. e.g. use `isAirAdmin` instead
  // of the user's `roles` array

  isAirAdmin: boolean;
  email: string;
}

export const AnonymousUserContext: ClientFeatureFlagUserContext = {
  kind: 'user',
  key: 'anonymous',
  anonymous: true,
  isAirAdmin: false,
  email: '',
};

export const buildClientFFUserContext = (user: LoggedInUser): ClientFeatureFlagUserContext => {
  return {
    kind: 'user',
    key: user.id,
    anonymous: false,
    isAirAdmin: user.userRoles.some(isAirAdminRole),
    email: user.email,
  };
};
